// src/components/tour/tours/clientManager.jsx
//
// Client Manager tour (internal admins only). ctx is registered in
// ClientManager.jsx with the real client list.

import React from 'react';
import { replayStep } from './common';

const StatusChip = ({ status }) =>
    status === 'pending' ? <span className="cm-status-chip cm-pending">PENDING</span>
    : status === 'active' ? <span className="cm-status-chip cm-active">ACTIVE</span>
    : <span className="cm-status-chip cm-inactive">INACTIVE</span>;

const RoleChip = ({ role, children }) => <span className={`cm-role-chip cm-${role}`}>{children}</span>;

export function getClientManagerSteps(ctx, env) {
    const {
        clients = [],
        tourClient,
        tourClientStatus,
        tourClientDeviceCount = 0,
        badgeCount = 0,
        orgCount = 0,
        openMobileAddPanel,
        closeMobileAddPanel,
        expandTourClient,
    } = ctx;
    const { isMobile } = env;

    const userTotal = clients.reduce((n, c) => n + (c.user_count || 0), 0);
    const pending = clients.filter((c) => !c.is_verified).length;
    const tourUsers = tourClient?.users || [];

    const addFormText = (
        <>
            <p>Enter the company name and a contact email. We email that contact a verification link.</p>
            <p>
                The client stays <StatusChip status="pending" /> until they verify, and can't be
                activated before that.
            </p>
        </>
    );

    return [
        {
            target: null,
            title: 'Welcome to Client Manager',
            content: (
                <>
                    <p>
                        Client companies and the people who can log in for them:{' '}
                        <span className="pt-data">{clients.length}</span> client{clients.length === 1 ? '' : 's'} with{' '}
                        <span className="pt-data">{userTotal}</span> user{userTotal === 1 ? '' : 's'}
                        {pending > 0 && <>, <span className="pt-data">{pending}</span> still waiting for email verification</>}.
                    </p>
                    <p>Which devices a client sees is decided in <b>Site Manager</b> by linking them to sites.</p>
                </>
            ),
        },

        { layout: 'desktop', target: '[data-tour="cm-add"]', title: 'Add a client', content: addFormText },
        {
            layout: 'mobile',
            target: '[data-tour="cm-add-toggle"]',
            onEnter: closeMobileAddPanel,
            title: 'Add a client',
            content: <p>Tap <b>+ ADD CLIENT</b> to open the form.</p>,
        },
        {
            layout: 'mobile',
            target: '[data-tour="cm-add-form"]',
            onEnter: openMobileAddPanel,
            title: 'The add form',
            content: addFormText,
        },

        {
            target: '[data-tour="cm-client"]',
            onEnter: isMobile ? closeMobileAddPanel : undefined,
            title: tourClient ? tourClient.client_name : 'A client',
            content: tourClient ? (
                <>
                    <p>
                        One {isMobile ? 'card' : 'row'} per client. This one is <StatusChip status={tourClientStatus} /> with{' '}
                        <span className="pt-data">{tourClient.router_count || 0}</span> router{(tourClient.router_count || 0) === 1 ? '' : 's'} and{' '}
                        <span className="pt-data">{tourClient.user_count || 0}</span> user{(tourClient.user_count || 0) === 1 ? '' : 's'}.
                    </p>
                    <p>
                        <StatusChip status="pending" /> = contact email not verified yet,{' '}
                        <StatusChip status="active" /> = in use,{' '}
                        <StatusChip status="inactive" /> = switched off by an admin.
                    </p>
                </>
            ) : null,
        },

        {
            target: '[data-tour="cm-client-actions"]',
            title: 'Client actions',
            content: (
                <>
                    <p><b>EDIT</b> the name or contact email. <b>+ USER</b> creates a login for someone at this client.</p>
                    <p>
                        <b>ACTIVATE / DEACTIVATE</b> switches the whole client on or off (only after email verification).{' '}
                        <b>{isMobile ? 'HIDE SL' : 'HIDE STARLINK'}</b> hides Starlink information from this client.
                    </p>
                    <p><b>EXPAND</b> shows its devices and users. <b>DELETE</b> removes the client completely.</p>
                </>
            ),
        },

        {
            target: '[data-tour="cm-client-detail"]',
            onEnter: expandTourClient,
            title: isMobile ? 'Users' : 'Devices & users',
            content: (
                <>
                    {!isMobile && (
                        <p>
                            <b>Devices</b> (<span className="pt-data">{tourClientDeviceCount}</span> here) come from the sites
                            linked to this client in Site Manager.
                        </p>
                    )}
                    <p>
                        <b>Users</b>{tourUsers.length > 0 && <> (<span className="pt-data">{tourUsers.length}</span>)</>} each have a role:{' '}
                        <RoleChip role="admin">ADMIN</RoleChip> full access, including their User Manager and changing
                        WAN priority / important items;{' '}
                        <RoleChip role="operator">OPERATOR</RoleChip> dashboards plus the Alert Dashboard;{' '}
                        <RoleChip role="viewer">VIEWER</RoleChip> view-only.
                    </p>
                    <p>
                        <b>RESET PW</b> emails a reset link. <b>NOTIF ON/OFF</b> controls push notifications
                        on their phone (viewers don't get any).
                    </p>
                </>
            ),
        },

        {
            target: '[data-tour="cm-badges"]',
            title: 'Badges',
            content: (
                <>
                    <p>
                        A badge is a short coloured label for a Peplink organization. It's shown next to that
                        organization's devices in the Alert Dashboard, so you can tell clients apart at a glance.
                    </p>
                    <p>
                        <span className="pt-data">{badgeCount}</span> of <span className="pt-data">{orgCount || badgeCount}</span> organizations
                        have one. The contrast check makes sure the text stays readable.
                    </p>
                </>
            ),
        },

        replayStep,
    ];
}
